import { createHash } from "node:crypto";
import { execFileSync } from "node:child_process";
import { closeSync, existsSync, openSync, readFileSync, readdirSync, readSync, statSync } from "node:fs";
import { homedir } from "node:os";
import { basename, join, resolve } from "node:path";

import { CLAUDE_PROJECTS_DIR, CODEX_SESSIONS_DIR } from "../constants.js";
import { readAiClientSqliteStore } from "./ai-client-sqlite-store.js";
import { isRecord } from "./fs.js";
import type {
  WorkGraphClientExtraction,
  WorkGraphClientExtractionFinding,
  WorkGraphSourceClient,
} from "./work-graph.js";
import type {
  ExtractionMode,
  RawEvent,
  RawEventKind,
  SkipReason,
  WorkGraphProvider,
} from "./work-graph-investigation.js";

const CLIENT_SOURCES = ["claude_code", "codex", "opencode", "goose", "cursor", "github", "slack"] as const;
type ClientSource = (typeof CLIENT_SOURCES)[number];

export interface LoadInvestigationSourceDataOptions {
  claudeProjectsDir?: string;
  codexSessionsDir?: string;
  cwd?: string;
  home?: string;
  limitPerSource?: number;
  maxBytesPerFile?: number;
  now?: Date;
  sinceDays?: number;
  sources: readonly ClientSource[];
}

export interface InvestigationSourceData {
  clientExtractions: WorkGraphClientExtraction[];
  connectedSources: string[];
  missingSources: string[];
  rawEvents: RawEvent[];
}

interface CandidateFile {
  extractionMode: ExtractionMode;
  mtimeMs: number;
  path: string;
  source: ClientSource;
}

interface ReadCandidateResult {
  collectionMethods: Array<"runtime_cli" | "local_store" | "structured_export">;
  events: RawEvent[];
  filesRead: number;
  filesSkipped: Array<{ path: string; reason: SkipReason }>;
  notes: string[];
  searchedSessions: number;
}

interface RuntimeCollectorResult extends ReadCandidateResult {
  collector: "runtime_cli";
}

const DEFAULT_LIMIT_PER_SOURCE = 8;
const DEFAULT_SINCE_DAYS = 45;
const DEFAULT_MAX_BYTES_PER_FILE = 2_000_000;
const WORK_SIGNAL_PATTERN =
  /\b(decision|decided|approved|assigned|tradeoff|artifact|implemented|created|changed|edited|shipped|verified|test|proof|commit|pr|blocked|blocker|failed|fails|error|timeout|permission|owner|handoff|mcp__|orgx_|tool call|schema|zod|next action|follow[- ]?up|initiative|agent|skill|recipe|workflow|customer|business|launch|deploy)\b/i;

export function parseInvestigationSourceList(value?: string): ClientSource[] {
  if (!value?.trim() || value.trim().toLowerCase() === "manual") return [];
  const requested = value
    .split(",")
    .map((item) => item.trim().toLowerCase().replace(/-/g, "_"))
    .filter(Boolean);
  const expanded = requested.includes("all")
    ? [...CLIENT_SOURCES]
    : requested.map((source) => source === "claude" ? "claude_code" : source);
  const deduped = [...new Set(expanded)];
  const invalid = deduped.filter((source) => !CLIENT_SOURCES.includes(source as ClientSource));
  if (invalid.length > 0) {
    throw new Error(
      `Unsupported investigation source: ${invalid.join(", ")}. Use claude, codex, opencode, goose, cursor, github, slack, or all.`,
    );
  }
  return deduped as ClientSource[];
}

function hash(value: unknown, length = 24): string {
  return createHash("sha256").update(JSON.stringify(value)).digest("hex").slice(0, length);
}

function expandPath(path: string, env: { cwd: string; home: string }): string {
  return path
    .replace(/^~(?=\/|$)/, env.home)
    .replace(/\$CWD/g, env.cwd)
    .replace(/\$HOME/g, env.home);
}

function safeStat(path: string) {
  try {
    return statSync(path);
  } catch {
    return null;
  }
}

function walkFiles(root: string, predicate: (path: string) => boolean, maxFiles = 500): string[] {
  if (!existsSync(root)) return [];
  const files: string[] = [];
  const stack = [root];
  const ignored = new Set(["node_modules", ".git", ".next", "dist", "build", ".turbo"]);
  while (stack.length > 0 && files.length < maxFiles) {
    const current = stack.pop();
    if (!current) continue;
    let entries: string[];
    try {
      entries = readdirSync(current);
    } catch {
      continue;
    }
    for (const entry of entries) {
      if (ignored.has(entry)) continue;
      const path = join(current, entry);
      const stats = safeStat(path);
      if (!stats) continue;
      if (stats.isDirectory()) {
        stack.push(path);
      } else if (stats.isFile() && predicate(path)) {
        files.push(path);
      }
    }
  }
  return files;
}

function providerForClient(client: ClientSource): WorkGraphProvider {
  if (client === "claude_code") return "anthropic";
  if (client === "codex") return "openai";
  return "unknown";
}

function workGraphSourceClient(client: ClientSource): WorkGraphSourceClient {
  if (client === "claude_code") return "claude-code";
  return client as WorkGraphSourceClient;
}

function clientLabel(client: ClientSource): string {
  switch (client) {
    case "claude_code":
      return "Claude Code sessions";
    case "codex":
      return "Codex sessions";
    case "opencode":
      return "OpenCode sessions";
    case "goose":
      return "goose sessions";
    case "cursor":
      return "Cursor workspace evidence";
    case "github":
      return "Git/GitHub proof";
    case "slack":
      return "Slack coordination";
  }
}

function redactedText(value: string, limit = 900): string {
  return value
    .replace(/-----BEGIN [^-]+-----[\s\S]*?-----END [^-]+-----/g, "[redacted-private-key]")
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "[redacted-email]")
    .replace(/\b(?:sk|pk)(?:-[A-Za-z0-9]+)+-[A-Za-z0-9_-]{12,}\b/g, "[redacted-token]")
    .replace(/\b(?:oxk|ghp|gho|xox[baprs]|npm|pat|hf)_[A-Za-z0-9_-]{12,}\b/g, "[redacted-token]")
    .replace(/\bglpat-[A-Za-z0-9_-]{12,}\b/g, "[redacted-token]")
    .replace(/\bAKIA[A-Z0-9]{16}\b/g, "[redacted-token]")
    .replace(/\bBearer\s+[A-Za-z0-9._~+/-]{12,}={0,2}\b/gi, "Bearer [redacted-token]")
    .replace(
      /((?:api[_-]?key|secret[_-]?key|access[_-]?token|refresh[_-]?token|password)\s*[:=]\s*["']?)[^\s"',}]{8,}/gi,
      "$1[redacted-secret]",
    )
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, limit);
}

function sourcePathRef(path: string): string {
  return path
    .replace(/^\/Users\/[^/]+(?=\/|$)/, "~")
    .replace(/^\/home\/[^/]+(?=\/|$)/, "~")
    .replace(/^[A-Za-z]:\\Users\\[^\\]+(?=\\|$)/, "~");
}

function nowWindow(timestamp: string, now: Date): RawEvent["occurred_in_window"] {
  const occurred = Date.parse(timestamp);
  if (!Number.isFinite(occurred)) return "older";
  const delta = now.getTime() - occurred;
  if (delta < 5 * 60 * 1000) return "live";
  if (delta <= 24 * 60 * 60 * 1000) return "recent_24h";
  if (delta <= 7 * 24 * 60 * 60 * 1000) return "recent_7d";
  if (delta <= 30 * 24 * 60 * 60 * 1000) return "recent_30d";
  return "older";
}

function makeRawEvent(input: {
  client: ClientSource;
  kind: RawEventKind;
  path: string;
  payload: Record<string, unknown>;
  rawByteOffset?: number | null;
  rawRowId?: string | null;
  role: RawEvent["role"];
  sessionId: string;
  timestamp: string;
  uriSuffix: string;
  now: Date;
}): RawEvent {
  const contentHash = hash(input.payload, 64);
  const uri = `${input.client}:${input.sessionId}:${input.uriSuffix}`;
  return {
    event_id: `evt_${hash([uri, contentHash], 24)}`,
    source_ref: {
      source_id: input.client,
      uri,
      cite_url: null,
      resolves: true,
    },
    source_id: input.client,
    provider: providerForClient(input.client),
    session_id: input.sessionId,
    project_id: null,
    timestamp: input.timestamp,
    occurred_in_window: nowWindow(input.timestamp, input.now),
    kind: input.kind,
    role: input.role,
    payload: input.payload,
    raw_byte_offset: input.rawByteOffset ?? null,
    raw_row_id: input.rawRowId ?? null,
    content_hash: contentHash,
    redaction_applied: true,
  };
}

function runRuntimeCli(command: string, args: string[], cwd: string): string | null {
  try {
    return execFileSync(command, args, {
      cwd,
      encoding: "utf8",
      stdio: ["ignore", "pipe", "ignore"],
      timeout: 5_000,
    });
  } catch {
    return null;
  }
}

function parseJsonLine(line: string): unknown {
  try {
    return JSON.parse(line);
  } catch {
    return null;
  }
}

function readTextWindow(path: string, stats: { size: number }, maxBytes: number): { text: string; truncated: boolean } {
  if (stats.size <= maxBytes) {
    return { text: readFileSync(path, "utf8"), truncated: false };
  }

  const bytesToRead = Math.min(stats.size, maxBytes);
  const buffer = Buffer.alloc(bytesToRead);
  const fd = openSync(path, "r");
  try {
    readSync(fd, buffer, 0, bytesToRead, stats.size - bytesToRead);
  } finally {
    closeSync(fd);
  }

  const raw = buffer.toString("utf8");
  const firstNewline = raw.indexOf("\n");
  return {
    text: firstNewline >= 0 ? raw.slice(firstNewline + 1) : raw,
    truncated: true,
  };
}

function asText(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        if (!isRecord(item)) return "";
        if (typeof item.text === "string") return item.text;
        if (typeof item.content === "string") return item.content;
        return "";
      })
      .filter(Boolean)
      .join("\n");
  }
  if (isRecord(value)) {
    if (typeof value.text === "string") return value.text;
    if (typeof value.content === "string") return value.content;
  }
  return "";
}

function stringField(record: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function timestampFor(record: unknown, statsMtime: number): string {
  if (isRecord(record)) {
    const timestamp = stringField(record, "timestamp", "created_at", "createdAt", "created", "time");
    if (timestamp && Number.isFinite(Date.parse(timestamp))) return new Date(timestamp).toISOString();
    const numeric = record.timestamp ?? record.created_at ?? record.created;
    if (typeof numeric === "number" && Number.isFinite(numeric)) {
      return new Date(numeric > 10_000_000_000 ? numeric : numeric * 1000).toISOString();
    }
  }
  return new Date(statsMtime).toISOString();
}

function mapOrgxRuntimePacket(record: unknown, fallbackTimestamp: string): Array<{
  kind: RawEventKind;
  role: RawEvent["role"];
  text: string;
  toolName?: string;
  timestamp: string;
}> {
  if (!isRecord(record) || record.type !== "orgx_work_graph_runtime_event") return [];
  const timestamp = timestampFor(record, Date.parse(fallbackTimestamp));
  const role = record.role === "user" || record.role === "assistant" || record.role === "tool" || record.role === "meta"
    ? record.role
    : "meta";
  const toolName = stringField(record, "tool_name", "toolName", "name") ?? undefined;
  const text =
    asText(record.content) ||
    asText(record.message) ||
    asText(record.text) ||
    asText(record.summary) ||
    redactedText(JSON.stringify(record));
  const requestedKind = stringField(record, "event_kind", "kind");
  const kind: RawEventKind = toolName
    ? requestedKind === "tool_call_error"
      ? "tool_call_error"
      : toolName.startsWith("mcp__")
        ? "mcp_tool_call"
        : "tool_call_start"
    : requestedKind === "decision"
      ? "assistant_reasoning"
      : requestedKind === "blocker"
        ? "tool_call_error"
        : requestedKind === "artifact"
          ? "file_edit"
          : requestedKind === "outcome"
            ? "test_run"
            : role === "user"
              ? "user_prompt"
              : "assistant_text";
  return [{
    kind,
    role,
    text,
    ...(toolName ? { toolName } : {}),
    timestamp,
  }];
}

function mapCodexRecord(record: unknown, fallbackTimestamp: string): Array<{
  kind: RawEventKind;
  role: RawEvent["role"];
  text: string;
  toolName?: string;
  timestamp: string;
}> {
  if (!isRecord(record)) return [];
  const timestamp = timestampFor(record, Date.parse(fallbackTimestamp));
  if (record.type === "response_item" && isRecord(record.payload)) {
    const payload = record.payload;
    if (payload.type === "message" && (payload.role === "user" || payload.role === "assistant")) {
      return [{
        kind: payload.role === "user" ? "user_prompt" : "assistant_text",
        role: payload.role,
        text: asText(payload.content),
        timestamp,
      }];
    }
    if (payload.type === "function_call") {
      const toolName = stringField(payload, "name", "tool_name") ?? "unknown_tool";
      return [{
        kind: toolName.startsWith("mcp__") ? "mcp_tool_call" : "tool_call_start",
        role: "tool",
        text: redactedText(JSON.stringify(payload.arguments ?? payload)),
        toolName,
        timestamp,
      }];
    }
    if (payload.type === "function_call_output") {
      const text = asText(payload.output) || redactedText(JSON.stringify(payload.output ?? payload));
      return [{
        kind: /\berror|failed|exception\b/i.test(text) ? "tool_call_error" : "tool_call_result",
        role: "tool",
        text,
        timestamp,
      }];
    }
    if (payload.type === "reasoning") {
      return [{ kind: "assistant_reasoning", role: "assistant", text: asText(payload.content), timestamp }];
    }
  }
  if (record.type === "event_msg" && isRecord(record.payload) && typeof record.payload.message === "string") {
    return [{ kind: "assistant_text", role: "meta", text: record.payload.message, timestamp }];
  }
  return [];
}

function mapClaudeRecord(record: unknown, fallbackTimestamp: string): Array<{
  kind: RawEventKind;
  role: RawEvent["role"];
  text: string;
  toolName?: string;
  timestamp: string;
}> {
  if (!isRecord(record)) return [];
  const timestamp = timestampFor(record, Date.parse(fallbackTimestamp));
  const message = isRecord(record.message) ? record.message : record;
  const role = message.role === "user" || message.role === "assistant" ? message.role : "meta";
  const content = message.content;
  const out: Array<{ kind: RawEventKind; role: RawEvent["role"]; text: string; toolName?: string; timestamp: string }> = [];
  if (Array.isArray(content)) {
    for (const [index, part] of content.entries()) {
      if (!isRecord(part)) continue;
      if (part.type === "text" || typeof part.text === "string") {
        out.push({ kind: role === "user" ? "user_prompt" : "assistant_text", role, text: asText(part), timestamp });
      } else if (part.type === "thinking") {
        out.push({ kind: "assistant_reasoning", role: "assistant", text: asText(part), timestamp });
      } else if (part.type === "tool_use") {
        const toolName = stringField(part, "name") ?? `tool_${index}`;
        out.push({
          kind: toolName.startsWith("mcp__") ? "mcp_tool_call" : "tool_call_start",
          role: "tool",
          text: redactedText(JSON.stringify(part.input ?? part)),
          toolName,
          timestamp,
        });
      } else if (part.type === "tool_result") {
        const text = asText(part.content) || redactedText(JSON.stringify(part.content ?? part));
        out.push({ kind: part.is_error === true ? "tool_call_error" : "tool_call_result", role: "tool", text, timestamp });
      }
    }
    return out;
  }
  const text = asText(content);
  if (text) out.push({ kind: role === "user" ? "user_prompt" : "assistant_text", role, text, timestamp });
  return out;
}

function readJsonlCandidate(candidate: CandidateFile, options: { maxBytesPerFile: number; now: Date }): ReadCandidateResult {
  const stats = safeStat(candidate.path);
  if (!stats) {
    return {
      collectionMethods: [],
      events: [],
      filesRead: 0,
      filesSkipped: [{ path: candidate.path, reason: "permission_denied" }],
      notes: [],
      searchedSessions: 0,
    };
  }
  const window = readTextWindow(candidate.path, stats, options.maxBytesPerFile);
  const lines = window.text.split(/\r?\n/);
  const fallbackTimestamp = new Date(stats.mtimeMs).toISOString();
  const sessionId = basename(candidate.path).replace(/\.[^.]+$/, "");
  const events: RawEvent[] = [];
  for (const [index, line] of lines.entries()) {
    if (!line.trim()) continue;
    const record = parseJsonLine(line);
    const runtimePacket = mapOrgxRuntimePacket(record, fallbackTimestamp);
    const mapped = runtimePacket.length > 0
      ? runtimePacket
      : candidate.source === "codex"
        ? mapCodexRecord(record, fallbackTimestamp)
        : candidate.source === "claude_code"
          ? mapClaudeRecord(record, fallbackTimestamp)
          : mapGenericJsonRecord(record, fallbackTimestamp);
    for (const [partIndex, item] of mapped.entries()) {
      if (!item.text && !item.toolName) continue;
      events.push(makeRawEvent({
        client: candidate.source,
        kind: item.kind,
        now: options.now,
        path: candidate.path,
        payload: {
          extraction_mode: candidate.extractionMode,
          collection_method: "local_store",
          relative_source_path: sourcePathRef(candidate.path),
          text_summary: redactedText(item.text),
          tool_name: item.toolName,
        },
        rawByteOffset: index,
        role: item.role,
        sessionId,
        timestamp: item.timestamp,
        uriSuffix: `line:${index}:part:${partIndex}`,
      }));
    }
  }
  return {
    collectionMethods: ["local_store"],
    events,
    filesRead: 1,
    filesSkipped: [],
    notes: window.truncated
      ? [`Read the latest ${options.maxBytesPerFile} bytes from oversized JSONL source instead of skipping it.`]
      : [],
    searchedSessions: 1,
  };
}

function mapGenericJsonRecord(record: unknown, fallbackTimestamp: string): Array<{
  kind: RawEventKind;
  role: RawEvent["role"];
  text: string;
  toolName?: string;
  timestamp: string;
}> {
  if (!isRecord(record)) return [];
  const timestamp = timestampFor(record, Date.parse(fallbackTimestamp));
  const roleValue = record.role === "user" || record.role === "assistant" ? record.role : "meta";
  const toolName = stringField(record, "tool_name", "toolName", "name");
  if (toolName) {
    const text = asText(record.response) || asText(record.output) || redactedText(JSON.stringify(record));
    return [{
      kind: toolName.startsWith("mcp__") ? "mcp_tool_call" : record.success === false ? "tool_call_error" : "tool_call_start",
      role: "tool",
      text,
      toolName,
      timestamp,
    }];
  }
  const text = asText(record.content) || asText(record.message) || asText(record.text);
  if (!text) return [];
  return [{
    kind: roleValue === "user" ? "user_prompt" : "assistant_text",
    role: roleValue,
    text,
    timestamp,
  }];
}

function readJsonCandidate(candidate: CandidateFile, options: { maxBytesPerFile: number; now: Date }): ReadCandidateResult {
  const stats = safeStat(candidate.path);
  if (!stats || stats.size > options.maxBytesPerFile) {
    return {
      collectionMethods: [],
      events: [],
      filesRead: 0,
      filesSkipped: [{ path: candidate.path, reason: stats ? "schema_version_unsupported" : "permission_denied" }],
      notes: [],
      searchedSessions: 0,
    };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(readFileSync(candidate.path, "utf8"));
  } catch {
    return {
      collectionMethods: [],
      events: [],
      filesRead: 0,
      filesSkipped: [{ path: candidate.path, reason: "binary_or_corrupt" }],
      notes: [],
      searchedSessions: 0,
    };
  }
  const records = flattenPotentialMessages(parsed).slice(0, 800);
  const fallbackTimestamp = new Date(stats.mtimeMs).toISOString();
  const sessionId = basename(candidate.path).replace(/\.[^.]+$/, "");
  const events = records.flatMap((record, index) =>
    mapGenericJsonRecord(record, fallbackTimestamp).map((item, partIndex) =>
      makeRawEvent({
        client: candidate.source,
        kind: candidate.source === "slack"
          ? "slack_message"
          : candidate.source === "github" && item.kind === "assistant_text"
            ? "pr_event"
            : item.kind,
        now: options.now,
        path: candidate.path,
        payload: {
          extraction_mode: candidate.extractionMode,
          collection_method: "local_store",
          relative_source_path: sourcePathRef(candidate.path),
          text_summary: redactedText(item.text),
          tool_name: item.toolName,
          actor: isRecord(record) ? stringField(record, "user", "username", "actor", "author", "sender") : undefined,
        },
        rawRowId: String(index),
        role: item.role,
        sessionId,
        timestamp: item.timestamp,
        uriSuffix: `json:${index}:part:${partIndex}`,
      }),
    ),
  );
  return {
    collectionMethods: ["local_store"],
    events,
    filesRead: 1,
    filesSkipped: [],
    notes: [],
    searchedSessions: 1,
  };
}

function flattenPotentialMessages(value: unknown): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const visit = (item: unknown, depth: number) => {
    if (depth > 8 || out.length >= 1_000) return;
    if (Array.isArray(item)) {
      for (const child of item) visit(child, depth + 1);
      return;
    }
    if (!isRecord(item)) return;
    if (
      typeof item.role === "string" ||
      typeof item.content === "string" ||
      typeof item.text === "string" ||
      typeof item.tool_name === "string" ||
      typeof item.toolName === "string"
    ) {
      out.push(item);
    }
    for (const value of Object.values(item)) {
      if (Array.isArray(value) || isRecord(value)) visit(value, depth + 1);
    }
  };
  visit(value, 0);
  return out;
}

function readMarkdownCandidate(candidate: CandidateFile, options: { maxBytesPerFile: number; now: Date }): ReadCandidateResult {
  const stats = safeStat(candidate.path);
  if (!stats || stats.size > options.maxBytesPerFile) {
    return {
      collectionMethods: [],
      events: [],
      filesRead: 0,
      filesSkipped: [{ path: candidate.path, reason: stats ? "schema_version_unsupported" : "permission_denied" }],
      notes: [],
      searchedSessions: 0,
    };
  }
  const text = readFileSync(candidate.path, "utf8");
  const sessionId = basename(candidate.path).replace(/\.[^.]+$/, "");
  const timestamp = new Date(stats.mtimeMs).toISOString();
  const event = makeRawEvent({
    client: candidate.source,
    kind: candidate.path.includes(".cursor/rules") || candidate.path.endsWith(".cursorrules") ? "session_start" : "assistant_text",
    now: options.now,
    path: candidate.path,
    payload: {
      extraction_mode: candidate.extractionMode,
      collection_method: "local_store",
      relative_source_path: sourcePathRef(candidate.path),
      text_summary: redactedText(text, 1_200),
      tool_usage_state: candidate.path.includes("mcp.json") ? "configured" : undefined,
    },
    role: "meta",
    sessionId,
    timestamp,
    uriSuffix: "markdown:0",
  });
  return {
    collectionMethods: ["local_store"],
    events: [event],
    filesRead: 1,
    filesSkipped: [],
    notes: candidate.extractionMode === "specstory" ? ["SpecStory evidence is repo-grounded and version-stable."] : [],
    searchedSessions: 1,
  };
}

function readGitReflogCandidate(candidate: CandidateFile, options: { maxBytesPerFile: number; now: Date }): ReadCandidateResult {
  const stats = safeStat(candidate.path);
  if (!stats || stats.size > options.maxBytesPerFile) {
    return {
      collectionMethods: [],
      events: [],
      filesRead: 0,
      filesSkipped: [{ path: candidate.path, reason: stats ? "schema_version_unsupported" : "permission_denied" }],
      notes: [],
      searchedSessions: 0,
    };
  }
  const lines = readFileSync(candidate.path, "utf8").split(/\r?\n/).filter(Boolean).slice(-250);
  const events: RawEvent[] = [];
  const fallbackTimestamp = new Date(stats.mtimeMs).toISOString();
  for (const [index, line] of lines.entries()) {
    const [meta = "", message = ""] = line.split("\t");
    const parts = meta.trim().split(/\s+/);
    const commitSha = parts[1] ?? parts[0] ?? "";
    if (!/^[0-9a-f]{12,40}$/i.test(commitSha)) continue;
    const timestampSeconds = Number(parts.at(-2));
    const timestamp = Number.isFinite(timestampSeconds)
      ? new Date(timestampSeconds * 1000).toISOString()
      : fallbackTimestamp;
    const actor = meta.match(/^[0-9a-f]{40}\s+[0-9a-f]{40}\s+(.+?)\s+\d+\s+[+-]\d{4}/i)?.[1]?.trim();
    const summary = message.trim() || `commit ${commitSha.slice(0, 12)}`;
    events.push(makeRawEvent({
      client: "github",
      kind: "commit",
      now: options.now,
      path: candidate.path,
      payload: {
        extraction_mode: candidate.extractionMode,
        collection_method: "local_store",
        relative_source_path: sourcePathRef(candidate.path),
        text_summary: redactedText(`Commit ${commitSha.slice(0, 12)}: ${summary}`),
        commit_sha: commitSha,
        actor,
        proof_kind: "commit_landed",
      },
      rawByteOffset: index,
      role: "meta",
      sessionId: "git-reflog-head",
      timestamp,
      uriSuffix: `reflog:${index}:${commitSha.slice(0, 12)}`,
    }));
  }
  return {
    collectionMethods: ["local_store"],
    events,
    filesRead: 1,
    filesSkipped: [],
    notes: ["Git reflog evidence is local commit proof; remote PR metadata still requires a GitHub export or API-backed source."],
    searchedSessions: 1,
  };
}

function collectGithubRuntimeCli(input: {
  cwd: string;
  limit: number;
  now: Date;
  sinceDays: number;
}): RuntimeCollectorResult | null {
  const insideRepo = runRuntimeCli("git", ["rev-parse", "--is-inside-work-tree"], input.cwd)?.trim() === "true";
  if (!insideRepo) return null;

  const since = `${input.sinceDays} days ago`;
  const log = runRuntimeCli("git", [
    "log",
    `--since=${since}`,
    `--max-count=${Math.max(1, input.limit)}`,
    "--format=%H%x1f%an%x1f%aI%x1f%s",
  ], input.cwd);
  const events: RawEvent[] = [];
  const notes = ["GitHub source collected through the local git runtime CLI before falling back to repository files."];
  if (log?.trim()) {
    for (const [index, line] of log.trim().split(/\r?\n/).entries()) {
      const [commitSha, actor, timestamp, subject] = line.split("\x1f");
      if (!commitSha || !timestamp || !subject) continue;
      events.push(makeRawEvent({
        client: "github",
        kind: "commit",
        now: input.now,
        path: input.cwd,
        payload: {
          extraction_mode: "terminal_log",
          collection_method: "runtime_cli",
          runtime_cli: "git log",
          text_summary: redactedText(`Commit ${commitSha.slice(0, 12)}: ${subject}`),
          commit_sha: commitSha,
          actor: actor ? redactedText(actor, 120) : undefined,
          proof_kind: "commit_landed",
        },
        rawRowId: String(index),
        role: "meta",
        sessionId: "git-log",
        timestamp,
        uriSuffix: `git-log:${index}:${commitSha.slice(0, 12)}`,
      }));
    }
  }

  const ghJson = runRuntimeCli("gh", [
    "pr",
    "list",
    "--state",
    "all",
    "--limit",
    String(Math.max(1, Math.min(30, input.limit))),
    "--json",
    "number,title,state,url,mergedAt,updatedAt,author,headRefName",
  ], input.cwd);
  if (ghJson?.trim()) {
    try {
      const prs = JSON.parse(ghJson) as unknown;
      if (Array.isArray(prs)) {
        for (const [index, pr] of prs.entries()) {
          if (!isRecord(pr)) continue;
          const title = typeof pr.title === "string" ? pr.title : `PR ${String(pr.number ?? index + 1)}`;
          const timestamp = typeof pr.mergedAt === "string" && pr.mergedAt
            ? pr.mergedAt
            : typeof pr.updatedAt === "string" && pr.updatedAt
              ? pr.updatedAt
              : input.now.toISOString();
          const author = isRecord(pr.author) && typeof pr.author.login === "string" ? pr.author.login : undefined;
          events.push(makeRawEvent({
            client: "github",
            kind: "pr_event",
            now: input.now,
            path: input.cwd,
            payload: {
              extraction_mode: "terminal_log",
              collection_method: "runtime_cli",
              runtime_cli: "gh pr list",
              text_summary: redactedText(`PR ${String(pr.number ?? index + 1)} ${String(pr.state ?? "UNKNOWN")}: ${title}`),
              actor: author,
              pr_number: pr.number,
              pr_state: pr.state,
              pr_url: pr.url,
              proof_kind: pr.state === "MERGED" ? "commit_landed" : "pull_request_seen",
            },
            rawRowId: String(index),
            role: "meta",
            sessionId: "gh-pr-list",
            timestamp,
            uriSuffix: `gh-pr:${String(pr.number ?? index + 1)}`,
          }));
        }
      }
    } catch {
      notes.push("gh pr list returned unreadable JSON; git commit evidence was still retained.");
    }
  } else {
    notes.push("GitHub CLI PR metadata was unavailable; install or authenticate gh to attach PR/review proof.");
  }

  if (events.length === 0) return null;
  return {
    collectionMethods: ["runtime_cli"],
    collector: "runtime_cli",
    events,
    filesRead: 0,
    filesSkipped: [],
    notes,
    searchedSessions: new Set(events.map((event) => event.session_id)).size,
  };
}

function discoverCandidates(source: ClientSource, env: { cwd: string; home: string }, sinceMs: number): CandidateFile[] {
  const candidates: CandidateFile[] = [];
  const addFiles = (paths: string[], extractionMode: ExtractionMode, predicate: (path: string) => boolean) => {
    for (const rootPattern of paths) {
      const expanded = expandPath(rootPattern, env);
      const root = expanded.includes("*") ? expanded.slice(0, expanded.indexOf("*")).replace(/\/$/, "") : expanded;
      const exactStats = safeStat(expanded);
      const found = exactStats?.isFile()
        ? [expanded]
        : walkFiles(root, predicate);
      for (const path of found) {
        const stats = safeStat(path);
        if (!stats || stats.mtimeMs < sinceMs) continue;
        candidates.push({ extractionMode, mtimeMs: stats.mtimeMs, path, source });
      }
    }
  };

  if (source === "codex") {
    addFiles([
      "$CWD/.orgx/work-graph/runtime-events/codex",
      CODEX_SESSIONS_DIR,
    ], "jsonl", (path) => path.endsWith(".jsonl"));
  } else if (source === "claude_code") {
    addFiles([
      "$CWD/.orgx/work-graph/runtime-events/claude-code",
      "$CWD/.orgx/work-graph/runtime-events/claude",
      CLAUDE_PROJECTS_DIR,
    ], "jsonl", (path) => path.endsWith(".jsonl"));
  } else if (source === "opencode") {
    addFiles([
      "~/.local/share/opencode/storage/session",
      "~/Library/Application Support/opencode/storage/session",
    ], "json", (path) => path.endsWith(".json"));
    addFiles([
      "~/.local/share/opencode/opencode.db",
      "~/Library/Application Support/opencode/opencode.db",
      "~/.local/share/opencode/storage/db.sqlite",
      "~/Library/Application Support/opencode/storage/db.sqlite",
    ], "sqlite", (path) => path.endsWith(".sqlite") || path.endsWith(".db"));
  } else if (source === "goose") {
    addFiles(["~/.local/share/goose/sessions"], "jsonl", (path) => path.endsWith(".jsonl"));
    addFiles([
      "~/.local/share/goose/logs",
      "~/.local/share/goose/data/sessions/sessions.db",
    ], "sqlite", (path) => path.endsWith(".db") || /llm_request\.\d+\.jsonl$/.test(path));
  } else if (source === "cursor") {
    addFiles([
      "$CWD/.specstory/history",
      "$CWD/.cursor/rules",
      "$CWD/.cursorrules",
      "$CWD/.cursor/mcp.json",
    ], "specstory", (path) =>
      path.endsWith(".md") || path.endsWith(".mdc") || path.endsWith(".json") || path.endsWith(".cursorrules"),
    );
    addFiles([
      "~/.cursor/chats",
      "~/Library/Application Support/Cursor/User/workspaceStorage",
      "~/.config/Cursor/User/workspaceStorage",
    ], "sqlite", (path) => path.endsWith("store.db") || path.endsWith("state.vscdb"));
  } else if (source === "github") {
    addFiles(["$CWD/.git/logs/HEAD"], "terminal_log", (path) => path.endsWith("/.git/logs/HEAD"));
    addFiles([
      "$CWD/.orgx/github",
      "$CWD/.github/work-graph",
      "~/.orgx/github",
    ], "json", (path) => path.endsWith(".json"));
  } else if (source === "slack") {
    addFiles([
      "$CWD/.orgx/slack",
      "$CWD/.slack",
      "~/.orgx/slack",
    ], "json", (path) => path.endsWith(".json"));
  }

  return candidates.sort((a, b) => {
    const priority = (candidate: CandidateFile) =>
      candidate.path.endsWith("/store.db") || candidate.path.endsWith("/opencode.db") ? 0 : 1;
    return priority(a) - priority(b) || b.mtimeMs - a.mtimeMs;
  });
}

function discoverOverrideCandidates(
  source: ClientSource,
  root: string,
  sinceMs: number,
): CandidateFile[] {
  const extractionMode: ExtractionMode = root.endsWith(".json") ? "json" : "jsonl";
  const files = walkFiles(root, (path) => path.endsWith(".jsonl") || path.endsWith(".json"));
  const candidates: CandidateFile[] = [];
  for (const path of files) {
    const stats = safeStat(path);
    if (!stats || stats.mtimeMs < sinceMs) continue;
    candidates.push({ extractionMode, mtimeMs: stats.mtimeMs, path, source });
  }
  return candidates.sort((a, b) => b.mtimeMs - a.mtimeMs);
}

function collectRuntimeCliSource(
  source: ClientSource,
  env: { cwd: string; home: string },
  options: { limit: number; now: Date; sinceDays: number },
): RuntimeCollectorResult | null {
  if (source === "github") {
    return collectGithubRuntimeCli({
      cwd: env.cwd,
      limit: options.limit,
      now: options.now,
      sinceDays: options.sinceDays,
    });
  }
  return null;
}

function findingTypeFromEvent(event: RawEvent): WorkGraphClientExtractionFinding["type"] | null {
  const text = `${event.payload.text_summary ?? ""}\n${event.payload.tool_name ?? ""}`;
  if (!WORK_SIGNAL_PATTERN.test(text)) return null;
  if (event.kind === "commit" || event.kind === "pr_event") return "artifact";
  if (event.kind === "slack_message") return /\b(decision|approved|owner|handoff|blocked|launch)\b/i.test(text)
    ? "decision"
    : "action";
  if (/\b(decision|decided|tradeoff|architecture|approval)\b/i.test(text)) return "decision";
  if (/\b(blocked|blocker|failed|fails|error|timeout|permission|schema|zod|invalid)\b/i.test(text)) return "blocker";
  if (/\b(artifact|implemented|created|changed|edited|commit|pr|file|shipped|deploy)\b/i.test(text)) return "artifact";
  if (/\b(test|verified|proof|outcome|result)\b/i.test(text)) return "outcome";
  if (/\b(customer|business|revenue|deal|buyer)\b/i.test(text)) return "business";
  if (/\b(agent|skill|recipe|tool|mcp__|orgx_)\b/i.test(text)) return "missed_orchestration_opportunity";
  return "action";
}

function titleFromEvent(event: RawEvent): string {
  const text = redactedText(String(event.payload.text_summary ?? event.payload.tool_name ?? "AI-client work signal"), 240);
  const first = text
    .replace(/^(decision|artifact|blocker|proof|next action|outcome)\s*:\s*/i, "")
    .split(/[.!?]\s/)[0]
    ?.trim();
  return (first || `${event.source_id} work signal`).slice(0, 110);
}

function extractionFromEvents(input: {
  client: ClientSource;
  collectionMethods: Array<"runtime_cli" | "local_store" | "structured_export">;
  events: readonly RawEvent[];
  filesRead: number;
  filesSkipped: Array<{ path: string; reason: SkipReason }>;
  notes: string[];
}): WorkGraphClientExtraction {
  const findings: WorkGraphClientExtractionFinding[] = [];
  const seen = new Set<string>();
  for (const event of input.events) {
    const type = findingTypeFromEvent(event);
    if (!type) continue;
    const dedupe = `${type}:${titleFromEvent(event).toLowerCase()}`;
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    findings.push({
      type,
      title: titleFromEvent(event),
      summary:
        type === "missed_orchestration_opportunity" && Number(event.payload.tool_name ? 1 : 0) === 0
          ? "The client evidence mentions agent/tool workflow but does not prove an invocation event."
          : redactedText(String(event.payload.text_summary ?? "Client work signal"), 260),
      source_client: workGraphSourceClient(input.client),
      source_id: event.session_id,
      source_label: clientLabel(input.client),
      evidence_ref: event.source_ref.uri,
      confidence: event.kind === "mcp_tool_call" || event.kind === "tool_call_start" ? 0.82 : 0.68,
      occurred_at: event.timestamp,
      redacted_verbatim: redactedText(String(event.payload.text_summary ?? ""), 360),
      privacy_state: "redacted",
      metadata: {
        event_id: event.event_id,
        event_kind: event.kind,
        extraction_mode: event.payload.extraction_mode,
        collection_method: event.payload.collection_method,
        runtime_cli: event.payload.runtime_cli,
        actor: event.payload.actor,
        commit_sha: event.payload.commit_sha,
        proof_kind: event.payload.proof_kind ?? (event.kind === "commit" ? "commit_landed" : undefined),
        owner_ref: event.kind === "slack_message" ? event.payload.actor : undefined,
        provider: event.provider,
        tool_name: event.payload.tool_name,
      },
    });
    if (findings.length >= 50) break;
  }
  return {
    schema_version: "2.0.0.investigation",
    extraction_id: `${input.client}:investigation:${hash([input.client, input.events.map((event) => event.event_id)], 12)}`,
    source_client: workGraphSourceClient(input.client),
    source_label: clientLabel(input.client),
    collection_methods: [...new Set(input.collectionMethods)].sort(),
    searched_sources: [...new Set(input.events.map((event) => String(event.payload.relative_source_path ?? event.session_id)))].slice(0, 30),
    search_queries: [
      {
        id: "full_session_inventory",
        lens: "Full-session deterministic inventory",
        query: "Read messages, tool calls, commands, configs, and repo-grounded session artifacts before deriving loop candidates.",
        result_count: input.events.length,
      },
    ],
    extraction_quality: {
      confidence: findings.length > 0 ? 0.74 : input.filesRead > 0 ? 0.32 : 0.18,
      searched_session_count: Math.max(input.filesRead, new Set(input.events.map((event) => event.session_id)).size),
      skipped_session_count: input.filesSkipped.length,
      notes: [
        ...new Set(input.notes),
        `${input.events.length} normalized raw events; ${findings.length} public-safe loop candidates.`,
        ...input.filesSkipped.map((file) => `Skipped ${file.path}: ${file.reason}`),
      ],
    },
    findings,
  };
}

export function loadWorkGraphInvestigationSourceData(
  options: LoadInvestigationSourceDataOptions,
): InvestigationSourceData {
  const now = options.now ?? new Date();
  const env = {
    cwd: options.cwd ? resolve(options.cwd) : process.cwd(),
    home: options.home ?? homedir(),
  };
  const sinceMs = now.getTime() - (options.sinceDays ?? DEFAULT_SINCE_DAYS) * 24 * 60 * 60 * 1000;
  const sinceDays = options.sinceDays ?? DEFAULT_SINCE_DAYS;
  const limit = Math.max(1, options.limitPerSource ?? DEFAULT_LIMIT_PER_SOURCE);
  const maxBytesPerFile = Math.max(1, options.maxBytesPerFile ?? DEFAULT_MAX_BYTES_PER_FILE);
  const rawEvents: RawEvent[] = [];
  const clientExtractions: WorkGraphClientExtraction[] = [];
  const connectedSources: string[] = [];
  const missingSources: string[] = [];

  const sourceOverrides = new Map<ClientSource, string>();
  if (options.codexSessionsDir) sourceOverrides.set("codex", resolve(options.codexSessionsDir));
  if (options.claudeProjectsDir) sourceOverrides.set("claude_code", resolve(options.claudeProjectsDir));

  for (const source of options.sources) {
    const overrideRoot = sourceOverrides.get(source);
    const runtimeCliResult = overrideRoot
      ? null
      : collectRuntimeCliSource(source, env, { limit, now, sinceDays });
    const candidates = (overrideRoot
      ? discoverOverrideCandidates(source, overrideRoot, sinceMs)
      : discoverCandidates(source, env, sinceMs)).slice(0, limit);
    if (!runtimeCliResult && candidates.length === 0) {
      missingSources.push(`${clientLabel(source)} store`);
      continue;
    }

    const sourceEvents: RawEvent[] = runtimeCliResult ? [...runtimeCliResult.events] : [];
    const collectionMethods: Array<"runtime_cli" | "local_store" | "structured_export"> = runtimeCliResult
      ? [...runtimeCliResult.collectionMethods]
      : [];
    const filesSkipped: Array<{ path: string; reason: SkipReason }> = [];
    let filesRead = runtimeCliResult?.filesRead ?? 0;
    const notes: string[] = runtimeCliResult?.notes ? [...runtimeCliResult.notes] : [];
    for (const candidate of candidates) {
      if (candidate.extractionMode === "sqlite") {
        if (candidate.source !== "opencode" && candidate.source !== "cursor" && candidate.source !== "goose") {
          filesSkipped.push({ path: sourcePathRef(candidate.path), reason: "schema_version_unsupported" });
          continue;
        }
        const sqlite = readAiClientSqliteStore(candidate.source, candidate.path, {
          limit,
          sinceMs,
        });
        for (const event of sqlite.events) {
          sourceEvents.push(makeRawEvent({
            client: candidate.source,
            kind: event.kind,
            now,
            path: candidate.path,
            payload: {
              extraction_mode: candidate.extractionMode,
              collection_method: "local_store",
              relative_source_path: sourcePathRef(candidate.path),
              text_summary: redactedText(event.text),
              tool_name: event.toolName,
              tool_status: event.toolStatus,
            },
            rawRowId: event.rawRowId,
            role: event.role,
            sessionId: event.sessionId,
            timestamp: event.timestamp,
            uriSuffix: `sqlite:${event.rawRowId}`,
          }));
        }
        notes.push(...sqlite.notes);
        if (sqlite.readable) {
          collectionMethods.push("local_store");
          filesRead += 1;
        } else {
          filesSkipped.push({ path: sourcePathRef(candidate.path), reason: "schema_version_unsupported" });
        }
        continue;
      }
      const result = candidate.source === "github" && candidate.extractionMode === "terminal_log"
        ? readGitReflogCandidate(candidate, { maxBytesPerFile, now })
        : candidate.path.endsWith(".jsonl")
          ? readJsonlCandidate(candidate, { maxBytesPerFile, now })
          : candidate.path.endsWith(".json") && !candidate.path.endsWith("mcp.json")
            ? readJsonCandidate(candidate, { maxBytesPerFile, now })
            : readMarkdownCandidate(candidate, { maxBytesPerFile, now });
      sourceEvents.push(...result.events);
      collectionMethods.push(...result.collectionMethods);
      filesRead += result.filesRead;
      filesSkipped.push(...result.filesSkipped.map((file) => ({
        ...file,
        path: sourcePathRef(file.path),
      })));
      notes.push(...result.notes);
    }

    rawEvents.push(...sourceEvents);
    clientExtractions.push(extractionFromEvents({
      client: source,
      collectionMethods,
      events: sourceEvents,
      filesRead,
      filesSkipped,
      notes,
    }));
    if (sourceEvents.length > 0 || filesRead > 0) {
      connectedSources.push(clientLabel(source));
    } else {
      missingSources.push(`${clientLabel(source)} readable events`);
    }
  }

  return {
    clientExtractions,
    connectedSources: [...new Set(connectedSources)],
    missingSources: [...new Set(missingSources)],
    rawEvents,
  };
}
